import type { SettlementDistrictView } from '@aztec-realms/shared-types';

import type { SettlementGridBounds } from './settlement-projection';
import type { SettlementResidentPlot } from './settlement-resident-plan';

export interface SettlementPublicPath {
  districtCode: string;
  points: { x: number; y: number }[];
}

/** Centerline clearance also contains a 0.44-cell-wide ground material stamp. */
export const settlementPublicPathClearance = 0.24;
export const settlementPublicPathBudget = 12;
export const settlementPublicPathDistrictBudget = 4;
export const settlementPublicPathMaxLength = 6;
const step = 0.25;
const maxGridNodes = 4096;
const maxPairSearches = 24;
type Point = SettlementPublicPath['points'][number];

function validBounds(bounds: SettlementGridBounds): boolean {
  return (
    Object.values(bounds).every((value) => Number.isFinite(value) && Math.abs(value) <= 1e6) &&
    bounds.maxX >= bounds.minX &&
    bounds.maxY >= bounds.minY
  );
}

function compareBounds(a: SettlementGridBounds, b: SettlementGridBounds): number {
  return a.minX - b.minX || a.minY - b.minY || a.maxX - b.maxX || a.maxY - b.maxY;
}

/** All routing segments are axis aligned, so bounding-box intersection is exact. */
function clearSegment(a: Point, b: Point, plots: readonly SettlementGridBounds[]): boolean {
  const c = settlementPublicPathClearance;
  return !plots.some(
    (plot) =>
      Math.max(a.x, b.x) >= plot.minX - c &&
      Math.min(a.x, b.x) <= plot.maxX + c &&
      Math.max(a.y, b.y) >= plot.minY - c &&
      Math.min(a.y, b.y) <= plot.maxY + c,
  );
}

function exterior(point: Point, plot: SettlementGridBounds): boolean {
  const dx = Math.max(plot.minX - point.x, 0, point.x - plot.maxX);
  const dy = Math.max(plot.minY - point.y, 0, point.y - plot.maxY);
  // A short approach to a real side, never a diagonal corner or imagined entrance.
  return Math.max(dx, dy) <= step + 1e-7 && (dx === 0 || dy === 0);
}

function simplify(points: Point[]): Point[] {
  return points.filter((point, index) => {
    const before = points[index - 1];
    const after = points[index + 1];
    return (
      !before ||
      !after ||
      !(
        (before.x === point.x && point.x === after.x) ||
        (before.y === point.y && point.y === after.y)
      )
    );
  });
}

function districtPaths(
  district: Pick<SettlementDistrictView, 'code' | 'bounds'>,
  plots: readonly SettlementGridBounds[],
): SettlementPublicPath[] {
  const { bounds } = district;
  const c = settlementPublicPathClearance;
  const startX = Math.ceil((bounds.minX + c) / step) * step;
  const startY = Math.ceil((bounds.minY + c) / step) * step;
  // District max values include the final cell; plot max values are exclusive corners.
  const columns = Math.floor((bounds.maxX + 1 - c - startX) / step) + 1;
  const rows = Math.floor((bounds.maxY + 1 - c - startY) / step) + 1;
  if (columns <= 0 || rows <= 0 || columns * rows > maxGridNodes) return [];
  const points = Array.from({ length: columns * rows }, (_, index) => ({
    x: startX + (index % columns) * step,
    y: startY + Math.floor(index / columns) * step,
  }));
  const walkable = points.map((point) => clearSegment(point, point, plots));
  const neighbors = points.map((point, index) => {
    if (!walkable[index]) return [];
    const x = index % columns;
    const y = Math.floor(index / columns);
    return [
      ...(x > 0 ? [index - 1] : []),
      ...(y > 0 ? [index - columns] : []),
      ...(x + 1 < columns ? [index + 1] : []),
      ...(y + 1 < rows ? [index + columns] : []),
    ].filter((next) => walkable[next] && clearSegment(point, points[next]!, plots));
  });
  const entries = plots.map((plot) =>
    points.flatMap((point, index) => (walkable[index] && exterior(point, plot) ? [index] : [])),
  );
  const pairs = plots
    .flatMap((a, first) =>
      plots.slice(first + 1).flatMap((b, offset) => {
        const second = first + offset + 1;
        const gap = Math.hypot(
          Math.max(a.minX - b.maxX, b.minX - a.maxX, 0),
          Math.max(a.minY - b.maxY, b.minY - a.maxY, 0),
        );
        return entries[first]!.length &&
          entries[second]!.length &&
          gap <= settlementPublicPathMaxLength
          ? [{ first, second, gap }]
          : [];
      }),
    )
    .sort((a, b) => a.gap - b.gap || a.first - b.first || a.second - b.second);
  const groups = plots.map((_, index) => index);
  const result: SettlementPublicPath[] = [];
  let searches = 0;
  for (const pair of pairs) {
    if (result.length >= settlementPublicPathDistrictBudget || searches >= maxPairSearches) break;
    if (groups[pair.first] === groups[pair.second]) continue;
    searches++;
    const destinations = new Set(entries[pair.second]);
    const previous = new Int32Array(points.length).fill(-1);
    const distance = new Int32Array(points.length);
    // Do not accept an identical exterior point as a connection between two buildings.
    const queue = entries[pair.first]!.filter((index) => !destinations.has(index));
    for (const index of queue) previous[index] = index;
    let end = -1;
    for (let head = 0; head < queue.length; head++) {
      const current = queue[head]!;
      if (destinations.has(current)) {
        end = current;
        break;
      }
      if (distance[current]! >= settlementPublicPathMaxLength / step) continue;
      for (const next of neighbors[current]!) {
        if (previous[next] !== -1) continue;
        previous[next] = current;
        distance[next] = distance[current]! + 1;
        queue.push(next);
      }
    }
    if (end < 0) continue;
    const route: Point[] = [points[end]!];
    while (previous[end] !== end) {
      end = previous[end]!;
      route.push(points[end]!);
    }
    result.push({ districtCode: district.code, points: simplify(route.reverse()) });
    const from = groups[pair.second];
    const to = groups[pair.first]!;
    for (let index = 0; index < groups.length; index++) {
      if (groups[index] === from) groups[index] = to;
    }
  }
  return result;
}

/**
 * Presentation-only sparse public ground: a bounded forest of short, verified routes between
 * real building exteriors. This never reserves cells, changes placement or invents island links.
 * Unknown/oversized geometry is omitted rather than approximated by a line across occupied land.
 * Districts have independent logical coordinates; obstacles never leak into another island.
 */
export function planSettlementPublicPaths(
  districts: readonly Pick<SettlementDistrictView, 'code' | 'bounds' | 'unlocked'>[],
  plots: readonly SettlementResidentPlot[],
  pathBudget = settlementPublicPathBudget,
): SettlementPublicPath[] {
  if (!Number.isFinite(pathBudget) || pathBudget < 1 || districts.length > 32 || plots.length > 512)
    return [];
  const budget = Math.min(Math.floor(pathBudget), settlementPublicPathBudget);
  const planned = [...districts]
    .sort((a, b) => a.code.localeCompare(b.code) || compareBounds(a.bounds, b.bounds))
    .flatMap((district) => {
      if (!district.unlocked || !validBounds(district.bounds)) return [];
      // Duplicate district identities are ambiguous, including conflicting lock/bound values.
      if (districts.filter(({ code }) => code === district.code).length !== 1) return [];
      const occupied = plots.filter(({ districtCode }) => districtCode === district.code);
      if (
        occupied.length < 2 ||
        occupied.length > 128 ||
        occupied.some(
          ({ bounds }) =>
            !validBounds(bounds) ||
            bounds.maxX === bounds.minX ||
            bounds.maxY === bounds.minY ||
            bounds.minX < district.bounds.minX ||
            bounds.minY < district.bounds.minY ||
            bounds.maxX > district.bounds.maxX + 1 ||
            bounds.maxY > district.bounds.maxY + 1,
        )
      )
        return [];
      const sorted = occupied.map(({ bounds }) => bounds).sort(compareBounds);
      const unique = sorted.filter(
        (plot, index) => index === 0 || compareBounds(plot, sorted[index - 1]!) !== 0,
      );
      if (unique.length < 2) return [];
      return [districtPaths(district, unique)];
    });
  const result: SettlementPublicPath[] = [];
  // Give every inhabited island a first connection before adding another to the same island.
  for (let index = 0; index < settlementPublicPathDistrictBudget; index++) {
    for (const paths of planned) {
      const path = paths[index];
      if (path && result.length < budget) result.push(path);
    }
  }
  return result;
}
